import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { API, inr } from "@/lib/api";
import { PriceTag } from "@/components/PriceTag";
import { holySrc } from "@/lib/holyPhotos";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CalendarDays, Flame, Radio, UserRound } from "lucide-react";

const fmt = (d) => new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
const daysTo = (d) => Math.max(0, Math.ceil((new Date(d) - new Date()) / 86400000));

export const LiveDepartures = () => {
  const [list, setList] = useState([]);
  useEffect(() => {
    const load = () => fetch(`${API}/public/departures`).then((r) => r.json()).then((d) => setList(Array.isArray(d) ? d : [])).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);
  if (!list.length) return null;
  return (
    <section id="departures" className="px-4 sm:px-8 py-16 max-w-6xl mx-auto scroll-mt-20" data-testid="live-departures">
      <p className="eyebrow text-gold text-center mb-2 flex items-center justify-center gap-2"><Radio className="w-3.5 h-3.5 text-red-400 animate-pulse" /> Live seat availability</p>
      <h2 className="font-serif text-2xl sm:text-3xl font-semibold text-white text-center mb-10">Upcoming group departures</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {list.map((g) => {
          const pct = g.seats_total ? Math.min(100, Math.round((g.booked / g.seats_total) * 100)) : 0;
          const low = g.seats_left !== null && g.seats_left <= 10;
          return (
            <Card key={g.id} data-testid={`departure-${g.id}`} className="overflow-hidden bg-card/40 border-border/60 card-hover flex flex-col">
              <div className="relative h-32">
                <img src={holySrc(g.photo || "kaaba")} alt={g.name} className="w-full h-full object-cover" />
                <span className="absolute top-2 right-2 text-[10px] bg-black/70 text-gold px-2 py-1 rounded-full">{daysTo(g.date)} days to go</span>
              </div>
              <div className="p-5 flex flex-col flex-1">
                <p className="font-serif font-semibold text-white">{g.name}</p>
                <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1"><CalendarDays className="w-3 h-3 text-gold" /> {fmt(g.date)}{g.package ? ` · ${g.package}` : ""}</p>
                {g.muallem_name && <p className="text-xs text-muted-foreground flex items-center gap-1"><UserRound className="w-3 h-3 text-gold" /> Muallem: {g.muallem_name}</p>}
                {g.price > 0 && <p data-testid={`departure-price-${g.id}`} className="mt-2 font-serif"><PriceTag price={g.price} mrp={g.mrp} until={g.offer_until} big /> <span className="text-[11px] font-sans text-muted-foreground">per person</span></p>}
                {g.seats_total ? (
                  <div className="mt-4">
                    <div className="h-2 rounded-full bg-white/10 overflow-hidden"><div className={`h-full rounded-full transition-all duration-700 ${low ? "bg-red-400" : "bg-gold"}`} style={{ width: `${pct}%` }} /></div>
                    <p data-testid={`departure-seats-${g.id}`} className={`text-xs mt-2 flex items-center gap-1 ${low ? "text-red-300 font-semibold" : "text-muted-foreground"}`}>
                      {low && <Flame className="w-3.5 h-3.5" />}
                      {g.seats_left === 0 ? "Fully booked — join the waitlist" : `Only ${g.seats_left} of ${g.seats_total} seats left`}
                    </p>
                  </div>
                ) : <p className="text-xs text-muted-foreground mt-4">{g.booked} pilgrims booked</p>}
                <Link to="/portal" className="mt-auto pt-4"><Button size="sm" data-testid={`departure-book-${g.id}`} className="w-full bg-gold text-[#0a2e22] hover:bg-gold/90 font-semibold text-xs">{g.seats_left === 0 ? "Join Waitlist" : "Reserve My Seat"}</Button></Link>
              </div>
            </Card>
          );
        })}
      </div>
    </section>
  );
};
